import classNames from 'classnames';
import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React from 'react';
import { CardBody } from 'reactstrap';
import { injectIntl, intlShape } from 'react-intl';

import { modelOf } from '../../../../prop-types';
import AccountStore from '../../../../store/AccountStore';
import AddressStore from '../../../../store/AddressStore';
import CheckoutStore from '../../../../store/CheckoutStore';
import FormFieldId from '../../../../types/FormFieldId';
import RequestState from '../../../../types/RequestState';
import AddressBookModal from '../AddressBookModal';
import CheckoutBillingForm from '../CheckoutBillingForm';
import CheckoutDeliveryForm from '../CheckoutDeliveryForm';
import CheckoutOrderExtrasForm from '../CheckoutOrderExtrasForm';
import CheckoutRegistration from '../CheckoutRegistration';
import CheckoutYourReferenceForm from '../CheckoutYourReferenceForm';
import { useDeliveryAddressBookActions } from '../useDeliveryAddressBookActions';
import { useRecipientCheckoutForms } from '../useRecipientCheckoutForms';

const CheckoutRecipientVisitorForms = ({
  checkoutStore,
  accountStore,
  addressStore,
  onOrderUpdate,
  intl,
}) => {
  const { order } = checkoutStore;
  const forms = useRecipientCheckoutForms(order);

  const { deliveryAddressDropdownField, handleAddressBookSave } =
    useDeliveryAddressBookActions({
      order,
      checkoutStore,
      addressStore,
      onOrderUpdate,
      intl,
      forms,
    });

  const handleUpdate = ({ withoutTimer }) => {
    onOrderUpdate({ withoutTimer });
  };

  const separatedDeliveryField = order.getCustomerField(
    FormFieldId.SEPARATE_DELIVERY_ADDRESS
  );

  const isCheckedSeparateDelivery = separatedDeliveryField?.value;

  const hasDeliveryAddressDropdown = !!deliveryAddressDropdownField;
  const showNewAddressButton =
    hasDeliveryAddressDropdown &&
    Boolean(accountStore?.isUserAllowedAddressModification);

  const handleNewDeliveryAddress = () => {
    checkoutStore.addressBookModal.toggle();
  };

  return (
    <CardBody
      className={classNames(
        'CheckoutRecipientVisitorForms',
        'CheckoutRecipientVisitorForms__body'
      )}
    >
      <CheckoutBillingForm
        onOrderUpdate={handleUpdate}
        billingForm={forms.billingForm}
      />
      {isCheckedSeparateDelivery &&
        (hasDeliveryAddressDropdown || order.deliveryFields.length > 0) && (
          <CheckoutDeliveryForm
            onOrderUpdate={handleUpdate}
            deliveryForm={forms.deliveryForm}
            showNewAddressButton={showNewAddressButton}
            onNewAddressClick={handleNewDeliveryAddress}
          />
        )}
      <CheckoutYourReferenceForm onOrderUpdate={handleUpdate} />
      <CheckoutOrderExtrasForm onOrderUpdate={handleUpdate} />
      {order.show_registration && <CheckoutRegistration />}
      {showNewAddressButton && (
        <AddressBookModal
          isOpen={checkoutStore.addressBookModal.isOpen}
          toggle={checkoutStore.addressBookModal.toggle}
          onSave={handleAddressBookSave}
          loading={addressStore?.state === RequestState.LOADING}
        />
      )}
    </CardBody>
  );
};

CheckoutRecipientVisitorForms.propTypes = {
  checkoutStore: modelOf(CheckoutStore).isRequired,
  accountStore: modelOf(AccountStore),
  addressStore: modelOf(AddressStore),
  onOrderUpdate: PropTypes.func.isRequired,
  intl: intlShape.isRequired,
};

export default inject(
  'checkoutStore',
  'accountStore',
  'addressStore'
)(injectIntl(observer(CheckoutRecipientVisitorForms)));
